import React from "react";
import { Link } from "react-router-dom";
import {
  fundingOptions,
  organisation,
  pedagogyItems,
} from "../../data/clearith";

export const PedagogyContent: React.FC = () => (
  <>
    <section className="cl-page-section">
      <div className="container">
        <div className="cl-page-intro">
          <span className="cl-kicker">Notre méthode</span>
          <h2 className="cl-section-title">
            Un parcours rythmé par l’interaction, la pratique et le suivi.
          </h2>
          <p className="cl-lead">
            L’accompagnement pédagogique vise à rendre chaque participant
            acteur de sa progression et à relier les apprentissages à des
            situations professionnelles.
          </p>
        </div>
        <div className="row g-4">
          {pedagogyItems.map((item) => (
            <div className="col-lg-4 col-md-6" key={item.number}>
              <article className="cl-method-card cl-method-card--light">
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
    <section className="cl-process-band">
      <div className="container">
        <div className="cl-process-band__grid">
          {[
            ["1", "Échange préalable", "Identifier le besoin et l’objectif professionnel."],
            ["2", "Positionnement", "Situer le niveau de départ et adapter le parcours."],
            ["3", "Formation", "Alterner apports, pratique et classes virtuelles."],
            ["4", "Évaluation", "Mesurer les acquis et préparer la certification."],
          ].map(([number, title, text]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  </>
);

export const FundingContent: React.FC = () => (
  <section className="cl-page-section">
    <div className="container">
      <div className="cl-page-intro">
        <span className="cl-kicker">Financer son parcours</span>
        <h2 className="cl-section-title">
          Plusieurs solutions peuvent être étudiées selon votre situation.
        </h2>
        <p className="cl-lead">
          L’existence d’un dispositif ne garantit pas la prise en charge. Toute
          demande reste soumise aux critères du financeur et à l’acceptation du
          dossier.
        </p>
      </div>
      <div className="cl-funding-grid">
        {fundingOptions.map((option, index) => (
          <article key={option.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h3>{option.title}</h3>
            <p>{option.text}</p>
          </article>
        ))}
      </div>
      <div className="cl-note-panel">
        <div>
          <span className="cl-kicker">Besoin d’aide ?</span>
          <h3>Présentez-nous votre situation professionnelle.</h3>
          <p>
            Nous vous transmettrons les informations disponibles sur les
            modalités et les démarches envisageables, sans engagement de prise
            en charge.
          </p>
        </div>
        <Link className="thm-btn agency-btn" to="/contact">
          <span className="text">Demander des informations</span>
        </Link>
      </div>
    </div>
  </section>
);

export const AccessibilityContent: React.FC = () => (
  <section className="cl-page-section">
    <div className="container">
      <div className="row gy-5">
        <div className="col-lg-7">
          <span className="cl-kicker">Accessibilité des formations</span>
          <h2 className="cl-section-title">
            Identifier les besoins en amont pour étudier les aménagements
            possibles.
          </h2>
          <p className="cl-lead">
            Les personnes en situation de handicap sont invitées à signaler
            leurs besoins au moment de la prise de contact. Un échange
            préalable permet d’examiner les adaptations pédagogiques,
            organisationnelles ou techniques qui peuvent être envisagées.
          </p>
          <div className="cl-access-steps">
            {[
              ["Échanger", "Décrire les besoins et les contraintes utiles à l’organisation du parcours."],
              ["Étudier", "Vérifier les aménagements possibles au regard de la formation à distance."],
              ["Organiser", "Formaliser les modalités retenues avant l’entrée en formation."],
              ["Suivre", "Faire le point pendant le parcours et ajuster lorsque cela est possible."],
            ].map(([title, text], index) => (
              <article key={title}>
                <span>{index + 1}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
        <div className="col-lg-5">
          <aside className="cl-info-panel cl-info-panel--sticky">
            <span className="cl-info-panel__label">Préparer votre demande</span>
            <h3>Parlons de vos besoins.</h3>
            <p>
              Contactez CLEARITH avant l’inscription afin que la faisabilité
              des aménagements puisse être étudiée dans de bonnes conditions.
            </p>
            <a className="cl-text-link" href={`mailto:${organisation.email}`}>
              {organisation.email} <span aria-hidden="true">↗</span>
            </a>
            <Link className="thm-btn agency-btn cl-summary-btn" to="/contact">
              <span className="text">Accéder au formulaire</span>
            </Link>
          </aside>
        </div>
      </div>
    </div>
  </section>
);

export const LegalContent: React.FC = () => (
  <section className="cl-page-section">
    <div className="container">
      <div className="cl-legal-content">
        <article>
          <h2>Éditeur du site</h2>
          <p>
            <strong>{organisation.name}</strong>
            <br />
            {organisation.legalForm}
            <br />
            Siège social : {organisation.address}
            <br />
            SIREN : {organisation.siren}
            <br />
            SIRET : {organisation.siret}
            <br />
            TVA intracommunautaire : {organisation.vatNumber}
            <br />
            E-mail :{" "}
            <a href={`mailto:${organisation.email}`}>{organisation.email}</a>
          </p>
        </article>
        <article>
          <h2>Organisme de formation</h2>
          <p>
            Numéro de déclaration d’activité :{" "}
            {organisation.activityDeclaration}
            <br />
            Région de déclaration : {organisation.declarationRegion}
            <br />
            Certification Qualiopi : {organisation.qualiopiScope}
            <br />
            Statut : {organisation.status}
            <br />
            Spécialité déclarée : {organisation.speciality}
          </p>
          <p>{organisation.legalNotice}</p>
        </article>
        <article>
          <h2>Direction de la publication</h2>
          <p>Le responsable de la publication est le représentant légal de CLEARITH.</p>
        </article>
        <article>
          <h2>Propriété intellectuelle</h2>
          <p>
            Les contenus, textes, éléments graphiques et la structure du site
            sont protégés par les règles applicables à la propriété
            intellectuelle. Toute reproduction ou adaptation non autorisée est
            interdite.
          </p>
        </article>
        <article>
          <h2>Responsabilité</h2>
          <p>
            CLEARITH veille à l’exactitude des informations publiées. Les
            modalités, tarifs et possibilités de financement peuvent évoluer ;
            leur confirmation intervient lors de l’étude de la demande.
          </p>
        </article>
      </div>
    </div>
  </section>
);

export const PrivacyContent: React.FC = () => (
  <section className="cl-page-section">
    <div className="container">
      <div className="cl-legal-content">
        <article>
          <h2>Responsable du traitement</h2>
          <p>
            CLEARITH, située au {organisation.address}, est responsable des
            données transmises dans le cadre des demandes d’information. Pour
            toute question :{" "}
            <a href={`mailto:${organisation.email}`}>{organisation.email}</a>.
          </p>
        </article>
        <article>
          <h2>Données concernées et finalités</h2>
          <p>
            Les informations saisies dans le formulaire servent à répondre à
            la demande, préciser le projet de formation, recontacter la personne
            et transmettre les informations utiles au parcours envisagé.
          </p>
        </article>
        <article>
          <h2>Base et destinataires</h2>
          <p>
            Le traitement repose sur le consentement exprimé avant l’envoi du
            formulaire et, lorsque la demande porte sur une inscription, sur
            les échanges précontractuels. Les informations sont destinées aux
            personnes habilitées au sein de CLEARITH.
          </p>
        </article>
        <article>
          <h2>Conservation et sécurité</h2>
          <p>
            Les données sont conservées pendant la durée nécessaire au
            traitement de la demande, puis selon les obligations applicables à
            la relation de formation. CLEARITH met en œuvre des mesures adaptées
            pour en limiter l’accès.
          </p>
        </article>
        <article>
          <h2>Vos droits</h2>
          <p>
            Vous pouvez demander l’accès, la rectification, l’effacement ou la
            limitation de vos données et retirer votre consentement en écrivant
            à {organisation.email}. Vous pouvez également saisir la CNIL si
            vous estimez que vos droits ne sont pas respectés.
          </p>
        </article>
        <article>
          <h2>Formulaire et services externes</h2>
          <p>
            Le formulaire prépare un e-mail dans le logiciel de messagerie de
            l’utilisateur. La carte de la page contact est fournie par un
            service cartographique externe susceptible d’appliquer ses propres
            règles de confidentialité.
          </p>
        </article>
      </div>
    </div>
  </section>
);
